import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BranchProvider } from './context/BranchContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { MotionConfig } from 'framer-motion';
import PolicyAcceptanceGate from './components/PolicyAcceptanceGate';
import { ClientProvider } from './context/ClientContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import StatusPage from './pages/StatusPage';
import NotFound from './pages/NotFound';
import NoAccess from './components/NoAccess';
import CookieConsent from './components/CookieConsent';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/Clients/ClientList';
import ClientLayout from './pages/Clients/ClientLayout';
import ClientDetail from './pages/Clients/ClientDetail';
import ClientForm from './pages/Clients/ClientForm';
import ClientCommunications from './pages/Clients/ClientCommunications';
import ClientEmail from './pages/Clients/ClientEmail';
import ClientBillingReport from './pages/Clients/ClientBillingReport';
import ClientCalendar from './pages/Clients/ClientCalendar';
import ClientDocuments from './pages/Clients/ClientDocuments';
import ClientCarePlan from './pages/Clients/ClientCarePlan';
import ClientFamilyPortal from './pages/Clients/ClientFamilyPortal';
import ClientGoals from './pages/Clients/ClientGoals';
import ClientGoalForm from './pages/Clients/ClientGoalForm';
import ClientQuoteForm from './pages/Clients/ClientQuoteForm';
import ClientForms from './pages/Clients/ClientForms';
import ClientCharts from './pages/Clients/ClientCharts';
import StaffList from './pages/Staff/StaffList';
import StaffForm from './pages/Staff/StaffForm';
import StaffDetail from './pages/Staff/StaffDetail';
import StaffDocuments from './pages/Staff/StaffDocuments';
import StaffCalendar from './pages/Staff/StaffCalendar';
import StaffLeave from './pages/Staff/StaffLeave';
import StaffTraining from './pages/Staff/StaffTraining';
import StaffWarnings from './pages/Staff/StaffWarnings';
import StaffCommunications from './pages/Staff/StaffCommunications';
import StaffArchived from './pages/Staff/StaffArchived';
import StaffDocumentShared from './pages/Staff/StaffDocumentShared';
import StaffDocumentExpired from './pages/Staff/StaffDocumentExpired';
import StaffContracts from './pages/Staff/StaffContracts';
import StaffNotes from './pages/Staff/StaffNotes';
import StaffAppraisal from './pages/Staff/StaffAppraisal';
import StaffIncidents from './pages/Staff/StaffIncidents';
import StaffAvailableShifts from './pages/Staff/StaffAvailableShifts';
import StaffEmployeeShifts from './pages/Staff/StaffEmployeeShifts';
import StaffAvailability from './pages/Staff/StaffAvailability';
import Scheduler from './pages/Scheduler/Scheduler';
import Templates from './pages/Templates/Templates';
import Groups from './pages/Groups/Groups';
import SilTemplate from './pages/Sil/SilTemplate';
import Invoices from './pages/Invoices/Invoices';
import Timesheets from './pages/Timesheets/Timesheets';
import Incidents from './pages/Incidents/Incidents';
import Tasks from './pages/Tasks/Tasks';
import Teams from './pages/Teams/Teams';
import ServiceAgreements from './pages/ServiceAgreements/ServiceAgreements';
import Reports from './pages/Reports/Reports';
import ReportHub from './pages/Reports/ReportHub';
import ShiftDetails from './pages/Reports/ShiftDetails';
import DeclinedShifts from './pages/Reports/DeclinedShifts';
import VacantShifts from './pages/Reports/VacantShifts';
import RecurringShiftExpiry from './pages/Reports/RecurringShiftExpiry';
import StaffDocumentExpiry from './pages/Reports/StaffDocumentExpiry';
import ClientDocumentExpiry from './pages/Reports/ClientDocumentExpiry';
import ClockVariance from './pages/Reports/ClockVariance';
import WorkHoursCompliance from './pages/Reports/WorkHoursCompliance';
import PayReviewDate from './pages/Reports/PayReviewDate';
import HumanCapitalVariance from './pages/Reports/HumanCapitalVariance';
import CarePlanProgress from './pages/Reports/CarePlanProgress';
import CarePlanActivity from './pages/Reports/CarePlanActivity';
import MedicationAdherence from './pages/Reports/MedicationAdherence';
import MedicationUsage from './pages/Reports/MedicationUsage';
import ClientFullReport from './pages/Reports/ClientFullReport';
import ActivityReport from './pages/Reports/ActivityReport';
import BillingReport from './pages/Reports/BillingReport';
import ReimbursementsReport from './pages/Reports/ReimbursementsReport';
import FundBalances from './pages/Reports/FundBalances';
import Performance from './pages/Reports/Performance';
import DeliveredVsInvoiced from './pages/Reports/DeliveredVsInvoiced';
import StaffCompliance from './pages/Reports/StaffCompliance';
import FundExpiry from './pages/Reports/FundExpiry';
import FundDepletion from './pages/Reports/FundDepletion';
import ClientOnboarding from './pages/Reports/ClientOnboarding';
import StaffOnboarding from './pages/Reports/StaffOnboarding';
import PayslipReport from './pages/Reports/PayslipReport';
import Certifications from './pages/Reports/Certifications';
import ClockInOutReport from './pages/Reports/ClockInOutReport';
import CareWorkerSchedule from './pages/Reports/CareWorkerSchedule';
import CareWorkerScheduleKmExp from './pages/Reports/CareWorkerScheduleKmExp';
import ClientListReport from './pages/Reports/ClientListReport';
import ClientNotesReport from './pages/Reports/ClientNotesReport';
import ClientServiceScheduleReport from './pages/Reports/ClientServiceScheduleReport';
import ClientServiceProgressNotes from './pages/Reports/ClientServiceProgressNotes';
import EmployeeNotesReport from './pages/Reports/EmployeeNotesReport';
import { StaffShiftNotesReport, ClientShiftNotesReport } from './pages/Reports/ShiftNotesReport';
import StaffWarningReport from './pages/Reports/StaffWarningReport';
import StaffWarningAcknowledgement from './pages/Reports/StaffWarningAcknowledgement';
import TimesheetReportAll from './pages/Reports/TimesheetReportAll';
import TimesheetApproved from './pages/Reports/TimesheetApproved';
import TimesheetApprovedRostered from './pages/Reports/TimesheetApprovedRostered';
import TimesheetPayRates from './pages/Reports/TimesheetPayRates';
import TimesheetQuery from './pages/Reports/TimesheetQuery';
import RadiusReport from './pages/Reports/RadiusReport';
import CarRegoReport from './pages/Reports/CarRegoReport';
import EmployeeComplianceExtended from './pages/Reports/EmployeeComplianceExtended';
import FleetUsageReport from './pages/Reports/FleetUsageReport';
import ClientChecklistReport from './pages/Reports/ClientChecklistReport';
import ClientProfileReport from './pages/Reports/ClientProfileReport';
import PlanManagedFunding from './pages/Reports/PlanManagedFunding';
import ServiceTypeReport from './pages/Reports/ServiceTypeReport';
import Vehicles from './pages/Vehicles/Vehicles';
import Medications from './pages/Medications/Medications';
import Forms from './pages/Forms/Forms';
import Messages from './pages/Messages/Messages';
import MailApp from './pages/Mail/MailApp';
import Leave from './pages/Leave/Leave';
import Reimbursements from './pages/Reimbursements/Reimbursements';
import JobBoard from './pages/JobBoard/JobBoard';
import Facilities from './pages/Facilities/Facilities';
import Settings from './pages/Settings/Settings';
import Billing from './pages/Billing/Billing';
import FeatureSettings from './pages/Settings/FeatureSettings';
import FamilyPortalSettings from './pages/Settings/FamilyPortalSettings';
import ReceptionistSettings from './pages/Settings/ReceptionistSettings';
import AiAutomation from './pages/Settings/AiAutomation';
import InvoiceSettings from './pages/Settings/InvoiceSettings';
import Branding from './pages/Settings/Branding';
import SmartMatch from './pages/Settings/SmartMatch';
import PriceBooks from './pages/Settings/PriceBooks';
import PayGroups from './pages/Settings/PayGroups';
import PayRates from './pages/Settings/PayRates';
import Allowances from './pages/Settings/Allowances';
import RolesPermissions from './pages/Settings/RolesPermissions';
import EVVDashboard from './pages/EVV/EVVDashboard';
import TrackingHub from './pages/LiveTracking/TrackingHub';
import Mileage from './pages/Mileage/Mileage';
import Payslips from './pages/Payslips/Payslips';
import PortalAccept from './pages/Portal/PortalAccept';
import PortalLogin from './pages/Portal/PortalLogin';
import PortalDashboard from './pages/Portal/PortalDashboard';
import ParticipantLogin from './pages/ParticipantPortal/ParticipantLogin';
import ParticipantAccept from './pages/ParticipantPortal/ParticipantAccept';
import ParticipantDashboard from './pages/ParticipantPortal/ParticipantDashboard';
import AgedCareLogin from './pages/AgedCarePortal/AgedCareLogin';
import AgedCareAccept from './pages/AgedCarePortal/AgedCareAccept';
import AgedCareDashboard from './pages/AgedCarePortal/AgedCareDashboard';
import ProviderPortalLogin from './pages/ProviderPortal/ProviderPortalLogin';
import ProviderPortalAccept from './pages/ProviderPortal/ProviderPortalAccept';
import ProviderPortalDashboard from './pages/ProviderPortal/ProviderPortalDashboard';
import ScIntakeForm from './pages/ScPublic/ScIntakeForm';
import ScReferralForm from './pages/Public/ScReferralForm';
import ScSurveyForm from './pages/ScPublic/ScSurveyForm';
import ScPortalLogin from './pages/ScPortal/ScPortalLogin';
import ScPortalAccept from './pages/ScPortal/ScPortalAccept';
import ScPortalDashboard from './pages/ScPortal/ScPortalDashboard';
import Locations from './pages/Settings/Locations';
import BranchDetail from './pages/Settings/BranchDetail';
import LeadsDashboard from './pages/Receptionist/LeadsDashboard';
// OrgReceptionistDashboard kept in the repo but no longer routed — /ai-receptionist
// now renders the role-aware LeadsDashboard. (intentionally not imported)
import ChatbotRedirect from './pages/Chatbot/ChatbotRedirect';
import Automations from './pages/Automations/Automations';
import FinancialsDashboard from './pages/Financials/FinancialsDashboard';
import ClientRates from './pages/Financials/ClientRates';
import StaffRates from './pages/Financials/StaffRates';
import Expenses from './pages/Financials/Expenses';
import FinConfig from './pages/Financials/FinConfig';
import TravelTransport from './pages/Financials/TravelTransport';
import PayContractors from './pages/Financials/PayContractors';
import ProfitReport from './pages/Financials/Reports/ProfitReport';
import StaffPayReport from './pages/Financials/Reports/StaffPayReport';
import ClientFunding from './pages/Financials/Reports/ClientFunding';
import StakeholderReport from './pages/Financials/Reports/StakeholderReport';
import KMReport from './pages/Financials/Reports/KMReport';
import ExpensesReport from './pages/Financials/Reports/ExpensesReport';
import AuditReport from './pages/Financials/Reports/AuditReport';
import ContractorReport from './pages/Financials/Reports/ContractorReport';
import MarketingOverview from './pages/Website/MarketingOverview';
import BlogPosts from './pages/Website/BlogPosts';
import BlogPostForm from './pages/Website/BlogPostForm';
import BlogCategories from './pages/Website/BlogCategories';
import Features from './pages/Website/Features';
import Testimonials from './pages/Website/Testimonials';
import FAQs from './pages/Website/FAQs';
import SiteSettings from './pages/Website/SiteSettings';
import PricingPlans from './pages/Website/PricingPlans';
import Pages from './pages/Website/Pages';
import HowItWorksEditor from './pages/Website/HowItWorksEditor';
import IntegrationsEditor from './pages/Website/IntegrationsEditor';
import ComplianceBadgesEditor from './pages/Website/ComplianceBadgesEditor';
import SocialProofEditor from './pages/Website/SocialProofEditor';
import VerifyEmail from './pages/VerifyEmail';
import SetPassword from './pages/Auth/SetPassword';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import ComplianceDashboard from './pages/Compliance/ComplianceDashboard';
import MigrationWizard from './pages/Migration/MigrationWizard';
import ExportData from './pages/Export/ExportData';
import ActivityLog from './pages/ActivityLog/ActivityLog';
import MarketDashboard from './pages/MarketIntel/MarketDashboard';
import CompetitorMatrix from './pages/MarketIntel/CompetitorMatrix';
import GapAnalysis from './pages/MarketIntel/GapAnalysis';
import ImprovementReport from './pages/MarketIntel/ImprovementReport';
import AgedCare from './pages/AgedCare/AgedCare';
import SupportCoordination from './pages/SupportCoordination/SupportCoordination';
import PlanManagement from './pages/PlanManagement/PlanManagement';
import ParticipantProfile from './pages/PlanManagement/ParticipantProfile';
import ProviderProfile from './pages/PlanManagement/ProviderProfile';
import ServiceContract from './pages/PlanManagement/ServiceContract';
import AccountSecurity from './pages/Account/AccountSecurity';
import { Eye, Shield, LogOut } from 'lucide-react';

function ProtectedRoute({ children, roles }) {
  const { user, loading, logout } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-400">Loading Rostery…</p>
      </div>
    </div>
  );
  if (!user) {
    // Keep where they were heading. Someone arriving from the pricing page with a plan
    // chosen would otherwise log in and be dropped on the dashboard, their choice gone.
    const next = window.location.pathname + window.location.search;
    const q = next && next !== '/' ? '?next=' + encodeURIComponent(next) : '';
    return <Navigate to={'/login' + q} replace />;
  }
  // Staff/carers belong in the mobile app — log them out immediately
  if (user.role === 'carer' || user.role === 'staff') {
    logout();
    return <Navigate to="/login?mobile=1" replace />;
  }
  if (user.role === 'super_admin') return <>{children}<PolicyAcceptanceGate /></>;
  // Say why instead of bouncing to the dashboard (which, for a role the dashboard itself excludes,
  // redirected '/' to itself).
  if (roles && !roles.includes(user.role)) return <><Layout><NoAccess reason="role" /></Layout><PolicyAcceptanceGate /></>;
  // Rendered alongside every authenticated page rather than at one entry point, so a deep link
  // straight into a roster still meets the gate. It renders nothing once the current Terms and
  // Privacy Policy have been accepted, and nothing at all if the check cannot be reached.
  return <>{children}<PolicyAcceptanceGate /></>;
}

// Blocks direct-URL access to a page whose org feature toggle is off — so a
// disabled page isn't just hidden from the sidebar, it's actually unreachable.
// `feature` is a feature_settings key, or an array meaning "at least one on"
// (e.g. the messaging flags). Super admins bypass org-level page gating.
function featureAllowed(feature, role, features) {
  if (!feature || role === 'super_admin') return true;
  const keys = Array.isArray(feature) ? feature : [feature];
  return keys.some(k => features[k] !== false);
}

function FeatureGate({ feature, children }) {
  const { user } = useAuth();
  const features = useFeatures();
  if (!featureAllowed(feature, user?.role, features)) return <Layout><NoAccess feature={feature} /></Layout>;
  return children;
}

// Pathless layout-route guard: wraps a whole group of routes (Reports,
// Financials, Market Intel) behind one feature toggle without per-route edits.
function FeatureOutlet({ feature }) {
  const { user } = useAuth();
  const features = useFeatures();
  // Signed-out visitors fall through to the child route's own sign-in redirect.
  if (user && !featureAllowed(feature, user.role, features)) {
    return <ProtectedRoute><Layout><NoAccess feature={feature} /></Layout></ProtectedRoute>;
  }
  return <Outlet />;
}

function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
            <Shield size={15} className="text-white" />
          </div>
          <span className="font-bold text-slate-900 text-base">Rostery</span>
          <span className="text-[11px] bg-indigo-100 text-indigo-700 px-2.5 py-0.5 rounded-full font-bold tracking-wide">SUPER ADMIN</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
            ← Back to App
          </button>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

function ImpersonationBanner() {
  const { user, exitImpersonation } = useAuth();
  if (!user?.impersonating) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white py-2 px-4 flex items-center justify-center gap-3 text-sm font-semibold shadow-lg">
      <Eye size={16} />
      <span>Viewing as: <strong>{user.name || user.email}</strong> (Org Admin Preview)</span>
      <button
        onClick={exitImpersonation}
        className="ml-2 bg-white text-amber-700 px-3 py-0.5 rounded-full text-xs font-bold hover:bg-amber-50 transition-colors">
        Exit Impersonation
      </button>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const A = ['admin','office_user'];
  const P = (el, roles, feature) => (
    <ProtectedRoute roles={roles}>
      <FeatureGate feature={feature}><Layout>{el}</Layout></FeatureGate>
    </ProtectedRoute>
  );

  return (
    <Routes>
      <Route path="/login"          element={user ? <Navigate to={user.role === 'super_admin' ? '/superadmin' : '/'} replace /> : <Login />} />
      {/* Organisation sign-up creates a request: email verification, then approval by Rostery (the super admin's approve step provisions the admin login). */}
      <Route path="/register"       element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/verify-email"   element={<VerifyEmail />} />
      <Route path="/set-password"   element={<SetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms"          element={<TermsOfService />} />
      <Route path="/status"         element={<StatusPage />} />

      {/* Core */}
      <Route path="/"          element={P(<Dashboard />, A)} />
      <Route path="/scheduler" element={P(<Scheduler />)} />
      <Route path="/templates" element={P(<Templates />, undefined, 'enable_templates')} />
      <Route path="/groups"    element={P(<Groups />, undefined, 'enable_groups')} />
      <Route path="/sil/templates/:id" element={P(<SilTemplate />)} />
      <Route path="/job-board" element={P(<JobBoard />)} />

      {/* Clients — list & admin */}
      <Route path="/clients"                      element={P(<ClientList />, A)} />
      <Route path="/clients/new"                  element={P(<ClientForm />, A)} />
      <Route path="/clients/archived"             element={P(<ClientList archived />, A)} />
      <Route path="/clients/teams"                element={P(<ClientList teams />, A)} />
      <Route path="/clients/documents/expired"    element={P(<ClientList expiredDocs />, A)} />

      {/* Clients — per-client sub-pages */}
      <Route path="/clients/:id/edit"             element={P(<ClientForm />, A)} />
      <Route path="/clients/:id/quotes/new"       element={P(<ClientQuoteForm />, A, 'enable_quotes')} />
      {/* A client's pages share one frame (ClientLayout loads the client once and keeps the header
          mounted), so switching tabs doesn't refetch or flash the header. */}
      <Route path="/clients/:id" element={P(<ClientLayout />, A)}>
        <Route index                  element={<ClientDetail />} />
        <Route path="care-plan"       element={<ClientCarePlan />} />
        <Route path="communications"  element={<ClientCommunications />} />
        <Route path="email"           element={<ClientEmail />} />
        <Route path="billing-report"  element={<ClientBillingReport />} />
        <Route path="calendar"        element={<ClientCalendar />} />
        <Route path="documents"       element={<ClientDocuments />} />
        <Route path="family-portal"   element={<ClientFamilyPortal />} />
        <Route path="goals"           element={<ClientGoals />} />
        <Route path="goals/new"       element={<ClientGoalForm />} />
        <Route path="goals/:goalId"   element={<ClientGoalForm />} />
        <Route path="forms"           element={<ClientForms />} />
        <Route path="charts"          element={<ClientCharts />} />
      </Route>

      {/* Staff */}
      <Route path="/staff"                        element={P(<StaffList />, A)} />
      <Route path="/staff/new"                    element={P(<StaffForm />, A)} />
      <Route path="/staff/archived"               element={P(<StaffArchived />, A)} />
      <Route path="/staff/teams"                  element={<Navigate to="/teams" replace />} />
      <Route path="/staff/documents/shared"       element={P(<StaffDocumentShared />, A)} />
      <Route path="/staff/documents/expired"      element={P(<StaffDocumentExpired />, A)} />
      <Route path="/staff/contracts"              element={P(<StaffContracts />, A)} />
      <Route path="/staff/:id"                    element={P(<StaffDetail />, A)} />
      <Route path="/staff/:id/edit"               element={P(<StaffForm />, A)} />
      <Route path="/staff/:id/documents"          element={P(<StaffDocuments />, A)} />
      <Route path="/staff/:id/calendar"           element={P(<StaffCalendar />, A)} />
      <Route path="/staff/:id/leave"              element={P(<StaffLeave />, A)} />
      <Route path="/staff/:id/training"           element={P(<StaffTraining />, A)} />
      <Route path="/staff/:id/warnings"           element={P(<StaffWarnings />, A)} />
      <Route path="/staff/:id/communications"     element={P(<StaffCommunications />, A)} />
      <Route path="/staff/:id/notes"             element={P(<StaffNotes />, A)} />
      <Route path="/staff/:id/appraisal"         element={P(<StaffAppraisal />, A)} />
      <Route path="/staff/:id/incidents"         element={P(<StaffIncidents />, A)} />
      <Route path="/staff/:id/available-shifts"  element={P(<StaffAvailableShifts />, A)} />
      <Route path="/staff/:id/employee-shifts"   element={P(<StaffEmployeeShifts />, A)} />
      <Route path="/staff/:id/availability"      element={P(<StaffAvailability />, A)} />

      {/* Facilities */}
      <Route path="/facilities" element={P(<Facilities />, A)} />

      {/* EVV */}
      <Route path="/evv" element={P(<EVVDashboard />, A)} />
      <Route path="/live-tracking" element={P(<TrackingHub initialTab="live" />, A)} />
      <Route path="/worker-activity" element={P(<TrackingHub initialTab="routes" />, A)} />
      <Route path="/mileage" element={P(<Mileage />, A, 'enable_mileage')} />
      <Route path="/payslips" element={P(<Payslips />, A, 'enable_payslips')} />

      {/* Care */}
      <Route path="/medications" element={P(<Medications />, undefined, 'enable_emar')} />
      <Route path="/forms"       element={P(<Forms />, A, 'enable_forms')} />
      <Route path="/incidents"   element={P(<Incidents />, A, 'enable_incidents')} />
      <Route path="/tasks"       element={P(<Tasks />, A, 'enable_tasks')} />
      <Route path="/teams"       element={P(<Teams />, ['admin','office_user'], 'enable_teams')} />
      <Route path="/service-agreements" element={P(<ServiceAgreements />, ['admin','office_user'])} />

      {/* Finance */}
      <Route path="/timesheets" element={P(<Timesheets />, A, 'enable_timesheets')} />
      <Route path="/invoices"   element={P(<Invoices />, A, 'enable_invoices')} />

      {/* Aged Care — HCP / Support at Home hub (tabs inside) */}
      <Route path="/aged-care"  element={P(<AgedCare />, A)} />
      {/* Support Coordination — NDIS CoS (engagements + dashboard tab inside) */}
      <Route path="/support-coordination" element={P(<SupportCoordination />, A)} />
      <Route path="/support-coordination/:scId" element={P(<SupportCoordination />, A)} />
      {/* Plan Management — provider invoices, budgets, NDIA claims */}
      <Route path="/plan-management" element={P(<PlanManagement />, A)} />
      <Route path="/plan-management/participants/:id" element={P(<ParticipantProfile />, A)} />
      <Route path="/plan-management/providers/:id"    element={P(<ProviderProfile />, A)} />
      <Route path="/plan-management/contracts/:planId" element={P(<ServiceContract />, A)} />

      {/* Team */}
      <Route path="/mail"      element={P(<MailApp />, A)} />
      <Route path="/messages"  element={P(<Messages />, undefined, ['enable_general_chat','enable_team_chats','enable_direct_messaging'])} />
      <Route path="/leave"     element={P(<Leave />)} />
      <Route path="/reimbursements" element={P(<Reimbursements />, undefined, 'enable_reimbursements')} />

      {/* Reports hub + sub-pages — whole group gated by the Reports page toggle */}
      <Route element={<FeatureOutlet feature="enable_reports" />}>
      <Route path="/reports"                             element={P(<Reports />, A)} />
      <Route path="/reports/hub"                         element={P(<ReportHub />, A)} />
      <Route path="/reports/shift-details"               element={P(<ShiftDetails />, A)} />
      <Route path="/reports/declined-shifts"             element={P(<DeclinedShifts />, A)} />
      <Route path="/reports/vacant-shifts"               element={P(<VacantShifts />, A)} />
      <Route path="/reports/recurring-shift-expiry"      element={P(<RecurringShiftExpiry />, A)} />
      <Route path="/reports/staff-document-expiry"       element={P(<StaffDocumentExpiry />, A)} />
      <Route path="/reports/client-document-expiry"      element={P(<ClientDocumentExpiry />, A)} />
      <Route path="/reports/clock-variance"              element={P(<ClockVariance />, A)} />
      <Route path="/reports/work-hours-compliance"       element={P(<WorkHoursCompliance />, A)} />
      <Route path="/reports/pay-review"                  element={P(<PayReviewDate />, A)} />
      <Route path="/reports/human-capital"               element={P(<HumanCapitalVariance />, A)} />
      <Route path="/reports/care-plan-progress"          element={P(<CarePlanProgress />, A)} />
      <Route path="/reports/care-plan-activity"          element={P(<CarePlanActivity />, A)} />
      <Route path="/reports/medication-adherence"        element={P(<MedicationAdherence />, A)} />
      <Route path="/reports/medication-usage"            element={P(<MedicationUsage />, A)} />
      <Route path="/reports/client-full-report"          element={P(<ClientFullReport />, A)} />
      <Route path="/reports/activity"                    element={P(<ActivityReport />, A)} />
      <Route path="/reports/billing"                     element={P(<BillingReport />, A)} />
      <Route path="/reports/reimbursements"              element={P(<ReimbursementsReport />, A)} />
      <Route path="/reports/fund-balances"               element={P(<FundBalances />, A)} />
      <Route path="/reports/performance"                 element={P(<Performance />, A)} />
      <Route path="/reports/exception/delivered-vs-invoiced" element={P(<DeliveredVsInvoiced />, A)} />
      <Route path="/reports/staff-compliance"               element={P(<StaffCompliance />, A)} />
      <Route path="/reports/fund-expiry"                   element={P(<FundExpiry />, A)} />
      <Route path="/reports/fund-depletion"                element={P(<FundDepletion />, A)} />
      <Route path="/reports/client-onboarding"             element={P(<ClientOnboarding />, A)} />
      <Route path="/reports/staff-onboarding"              element={P(<StaffOnboarding />, A)} />
      <Route path="/reports/payslip-report"               element={P(<PayslipReport />, A)} />
      <Route path="/reports/certifications"                   element={P(<Certifications />, A)} />
      <Route path="/reports/clock-in-out"                      element={P(<ClockInOutReport />, A)} />
      <Route path="/reports/care-worker-schedule"              element={P(<CareWorkerSchedule />, A)} />
      <Route path="/reports/care-worker-schedule-km-exp"       element={P(<CareWorkerScheduleKmExp />, A)} />
      <Route path="/reports/client-list"                        element={P(<ClientListReport />, A)} />
      <Route path="/reports/client-notes"                       element={P(<ClientNotesReport />, A)} />
      <Route path="/reports/client-service-schedule"            element={P(<ClientServiceScheduleReport />, A)} />
      <Route path="/reports/client-service-progress-notes"      element={P(<ClientServiceProgressNotes />, A)} />
      <Route path="/reports/employee-notes"                    element={P(<EmployeeNotesReport />, A)} />
      <Route path="/reports/staff-shift-notes"                 element={P(<StaffShiftNotesReport />, A)} />
      <Route path="/reports/client-shift-notes"                element={P(<ClientShiftNotesReport />, A)} />
      <Route path="/reports/staff-warning-report"              element={P(<StaffWarningReport />, A)} />
      <Route path="/reports/staff-warning-acknowledgement"     element={P(<StaffWarningAcknowledgement />, A)} />
      <Route path="/reports/timesheet-report"                  element={P(<TimesheetReportAll />, A)} />
      <Route path="/reports/timesheet-approved"                element={P(<TimesheetApproved />, A)} />
      <Route path="/reports/timesheet-approved-rostered"       element={P(<TimesheetApprovedRostered />, A)} />
      <Route path="/reports/timesheet-pay-rates"               element={P(<TimesheetPayRates />, A)} />
      <Route path="/reports/timesheet-query"                   element={P(<TimesheetQuery />, A)} />
      <Route path="/reports/radius"                            element={P(<RadiusReport />, A)} />
      <Route path="/reports/car-rego"                          element={P(<CarRegoReport />, A)} />
      <Route path="/reports/compliance-extended"               element={P(<EmployeeComplianceExtended />, A)} />
      <Route path="/reports/fleet-usage"                        element={P(<FleetUsageReport />, A)} />
      <Route path="/reports/client-checklist"                   element={P(<ClientChecklistReport />, A)} />
      <Route path="/reports/client-profile"                     element={P(<ClientProfileReport />, A)} />
      <Route path="/reports/plan-managed-funding"               element={P(<PlanManagedFunding />, A)} />
      <Route path="/reports/service-type"                       element={P(<ServiceTypeReport />, A)} />
      </Route>

      {/* Fleet management */}
      <Route path="/vehicles" element={P(<Vehicles />, A, 'enable_fleet')} />

      <Route path="/billing"                element={P(<Billing />, ['admin'])} />
      {/* Every web role: two-factor sign-in is the person's own, and Settings is admin-only. */}
      <Route path="/account/security"       element={P(<AccountSecurity />)} />
      <Route path="/settings"               element={P(<Settings />, ['admin'])} />
      <Route path="/settings/features"      element={P(<FeatureSettings />, ['admin'])} />
      <Route path="/settings/family-portal" element={P(<FamilyPortalSettings />, ['admin'])} />
      <Route path="/settings/ai-receptionist" element={P(<ReceptionistSettings />, ['admin'])} />
      <Route path="/settings/ai-automation" element={P(<AiAutomation />, ['admin'])} />
      <Route path="/settings/invoices"      element={P(<InvoiceSettings />, ['admin'])} />
      <Route path="/settings/branding"      element={P(<Branding />, ['admin'])} />
      <Route path="/settings/smart-match"   element={P(<SmartMatch />, ['admin'])} />
      <Route path="/settings/prices"        element={P(<PriceBooks />, ['admin'])} />
      <Route path="/settings/pay-groups"    element={P(<PayGroups />, ['admin'])} />
      <Route path="/settings/pay-rates"     element={P(<PayRates />, ['admin'])} />
      <Route path="/settings/allowances"    element={P(<Allowances />, ['admin'])} />
      <Route path="/settings/roles"         element={P(<RolesPermissions />, ['admin'])} />
      <Route path="/settings/locations"    element={P(<Locations />, ['admin'])} />
      <Route path="/settings/locations/:id" element={P(<BranchDetail />, ['admin'])} />
      <Route path="/compliance"             element={P(<ComplianceDashboard />, ['super_admin'])} />
      <Route path="/migration"              element={P(<MigrationWizard />, A)} />
      <Route path="/export-data"            element={P(<ExportData />, ['super_admin'])} />
      <Route path="/activity"               element={P(<ActivityLog />, ['admin','office_user'], 'enable_activity_log')} />
      <Route path="/superadmin" element={
        <ProtectedRoute roles={['super_admin']}>
          <SuperAdminLayout><SuperAdminDashboard /></SuperAdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/receptionist"           element={P(<LeadsDashboard />, ['super_admin'])} />
      <Route path="/receptionist/leads/:id" element={P(<LeadsDashboard />, ['super_admin'])} />
      {/* Org route now renders the SAME role-aware LeadsDashboard (self-scopes by role). */}
      <Route path="/ai-receptionist"        element={P(<LeadsDashboard />, ['admin','office_user'])} />
      <Route path="/chatbot"                element={P(<ChatbotRedirect />, ['admin','office_user','super_admin'])} />
      <Route path="/automations"            element={P(<Automations />, ['admin','office_user','super_admin'])} />

      {/* Financials — Pay Contractors has its own toggle; the rest are gated
          together by the Financials page toggle. */}
      <Route path="/financials/pay-contractors"          element={P(<PayContractors />, A, 'enable_pay_contractors')} />
      <Route element={<FeatureOutlet feature="enable_financials" />}>
      <Route path="/financials"                          element={P(<FinancialsDashboard />, A)} />
      <Route path="/financials/client-rates"             element={P(<ClientRates />, A)} />
      <Route path="/financials/staff-rates"              element={P(<StaffRates />, A)} />
      <Route path="/financials/expenses"                 element={P(<Expenses />, A)} />
      <Route path="/financials/config"                   element={P(<FinConfig />, A)} />
      <Route path="/financials/travel-transport"         element={P(<TravelTransport />, A)} />
      <Route path="/financials/reports/profit"           element={P(<ProfitReport />, A)} />
      <Route path="/financials/reports/staff-pay"        element={P(<StaffPayReport />, A)} />
      <Route path="/financials/reports/client-funding"   element={P(<ClientFunding />, A)} />
      <Route path="/financials/reports/stakeholders"     element={P(<StakeholderReport />, A)} />
      <Route path="/financials/reports/km"              element={P(<KMReport />, A)} />
      <Route path="/financials/reports/expenses"        element={P(<ExpensesReport />, A)} />
      <Route path="/financials/reports/audit"           element={P(<AuditReport />, A)} />
      <Route path="/financials/reports/contractors"     element={P(<ContractorReport />, A)} />
      </Route>

      {/* Website CMS */}
      <Route path="/website"                   element={P(<MarketingOverview />, ['super_admin'])} />
      <Route path="/website/blog"              element={P(<BlogPosts />, ['super_admin'])} />
      <Route path="/website/blog/new"          element={P(<BlogPostForm />, ['super_admin'])} />
      <Route path="/website/blog/:id/edit"     element={P(<BlogPostForm />, ['super_admin'])} />
      <Route path="/website/blog/categories"   element={P(<BlogCategories />, ['super_admin'])} />
      <Route path="/website/features"          element={P(<Features />, ['super_admin'])} />
      <Route path="/website/testimonials"      element={P(<Testimonials />, ['super_admin'])} />
      <Route path="/website/faqs"              element={P(<FAQs />, ['super_admin'])} />
      <Route path="/website/settings"          element={P(<SiteSettings />, ['super_admin'])} />
      <Route path="/website/pricing"           element={P(<PricingPlans />, ['super_admin'])} />
      <Route path="/website/pages"             element={P(<Pages />, ['super_admin'])} />
      <Route path="/website/how-it-works"      element={P(<HowItWorksEditor />, ['super_admin'])} />
      <Route path="/website/integrations"      element={P(<IntegrationsEditor />, ['super_admin'])} />
      <Route path="/website/compliance"        element={P(<ComplianceBadgesEditor />, ['super_admin'])} />
      <Route path="/website/social-proof"      element={P(<SocialProofEditor />, ['super_admin'])} />

      {/* Market Intelligence — whole group gated by the Market Intel page toggle */}
      <Route element={<FeatureOutlet feature="enable_market_intel" />}>
      <Route path="/market-intel"                    element={P(<MarketDashboard />, ['admin'])} />
      <Route path="/market-intel/competitors"        element={P(<CompetitorMatrix />, ['admin'])} />
      <Route path="/market-intel/gap-analysis"       element={P(<GapAnalysis />, ['admin'])} />
      <Route path="/market-intel/improvement-report" element={P(<ImprovementReport />, ['admin'])} />
      </Route>

      {/* Family portal — standalone layout (no sidebar) */}
      <Route path="/portal/accept/:token" element={<PortalAccept />} />
      <Route path="/portal/login"         element={<PortalLogin />} />
      <Route path="/portal/dashboard"     element={<PortalDashboard />} />

      {/* Participant (NDIS budget) portal — SEPARATE from the family portal: plan-managed
          participants sign in here for live budgets + invoice approvals */}
      <Route path="/participant/accept/:token" element={<ParticipantAccept />} />
      <Route path="/participant/login"          element={<ParticipantLogin />} />
      <Route path="/participant"                element={<ParticipantDashboard />} />

      {/* Support at Home (aged care) participant/family portal — SEPARATE from PM + family portals:
          SaH participants (or their family/nominee) sign in for live budget, statements + services */}
      <Route path="/aged-care-portal/accept/:token" element={<AgedCareAccept />} />
      <Route path="/aged-care-portal/login"          element={<AgedCareLogin />} />
      <Route path="/aged-care-portal"                element={<AgedCareDashboard />} />

      {/* Provider payment portal — providers self-serve invoice/payment status */}
      <Route path="/provider-portal/accept/:token" element={<ProviderPortalAccept />} />
      <Route path="/provider-portal/login"          element={<ProviderPortalLogin />} />
      <Route path="/provider-portal"                element={<ProviderPortalDashboard />} />

      {/* Support Coordination — public referral intake + feedback survey (token-authenticated) */}
      <Route path="/sc-intake/:token" element={<ScIntakeForm />} />
      {/* SC workflow v1 (6): the online referral forms sent to referrers and providers (token-addressed). */}
      <Route path="/forms/referral/:token" element={<ScReferralForm />} />
      <Route path="/sc-survey/:token" element={<ScSurveyForm />} />

      {/* Support Coordination — participant/nominee portal (own login) */}
      <Route path="/sc-portal/accept/:token" element={<ScPortalAccept />} />
      <Route path="/sc-portal/login"          element={<ScPortalLogin />} />
      <Route path="/sc-portal"                element={<ScPortalDashboard />} />

      <Route path="*" element={user ? P(<NotFound />) : <NotFound bare />} />
    </Routes>
  );
}

export default function App() {
  return (
    // Every framer-motion animation respects the operating system's "reduce motion" setting.
    <MotionConfig reducedMotion="user">
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
        <FeatureProvider>
        <ClientProvider>
        <BranchProvider>
          <ImpersonationBanner />
          <AppRoutes />
          <CookieConsent />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,.12)', fontSize: 14 },
              success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }}
          />
        </BranchProvider>
        </ClientProvider>
        </FeatureProvider>
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
    </MotionConfig>
  );
}
